import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { GoogleReviewCta } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _cache = null;
let _promise = null;
const loadReviews = () => {
  if (_cache) return Promise.resolve(_cache);
  if (!_promise) {
    _promise = axios
      .get(`${API}/google-reviews`)
      .then((r) => {
        _cache = r.data;
        return _cache;
      })
      .catch(() => null);
  }
  return _promise;
};

// Live GBP rating/count for headers ("5.0 on Google · 34 reviews")
export function useGoogleReviews() {
  const [data, setData] = useState(_cache);
  useEffect(() => {
    let on = true;
    loadReviews().then((d) => on && d && setData(d));
    return () => {
      on = false;
    };
  }, []);
  return data;
}

const Stars = ({ n = 5, size = 12 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star key={`s${i + 1}`} size={size} className={i < n ? "text-[#E8C766] fill-[#E8C766]" : "text-neutral-700"} />
    ))}
  </span>
);

const clamp = { display: "-webkit-box", WebkitLineClamp: 6, WebkitBoxOrient: "vertical", overflow: "hidden" };

// Live Google Business Profile reviews feed with attribution (Places API via backend)
export function GoogleReviewsFeed({ className = "" }) {
  const data = useGoogleReviews();
  if (!data) return <GoogleReviewCta className={className} />;
  return (
    <div className={className} data-testid="google-reviews-feed">
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <span className="font-heading font-bold text-5xl text-white leading-none" data-testid="google-reviews-rating">
          {Number(data.rating).toFixed(1)}
        </span>
        <span className="pb-1">
          <Stars size={15} />
          <span className="block mt-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-400" data-testid="google-reviews-count">
            Based on {data.count} Google reviews
          </span>
        </span>
      </div>
      <div className="mt-6 grid md:grid-cols-3 lg:grid-cols-5 gap-4">
        {data.reviews.slice(0, 5).map((r, i) => (
          <figure key={r.author + i} className="h-full bg-[#121214] border border-white/8 p-5 flex flex-col" data-testid={`google-review-card-${i}`}>
            <div className="flex items-center gap-2.5">
              {r.photo ? (
                <img src={r.photo} alt="" referrerPolicy="no-referrer" loading="lazy" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-[#9D0423] text-white flex items-center justify-center font-heading font-bold text-sm">
                  {r.author.charAt(0)}
                </span>
              )}
              <figcaption className="min-w-0">
                <a
                  href={r.author_url || data.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-[13px] font-semibold text-white truncate hover:text-[#E56A79] transition-colors"
                >
                  {r.author}
                </a>
                <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-neutral-500">{r.when}</span>
              </figcaption>
            </div>
            <div className="mt-3">
              <Stars n={r.rating} />
            </div>
            <blockquote className="mt-2.5 text-[12.5px] text-neutral-300 leading-relaxed flex-1" style={clamp}>
              {r.text}
            </blockquote>
          </figure>
        ))}
      </div>
      <p className="mt-3 text-[10.5px] text-neutral-600">Reviews shown as provided by Google, ordered by relevance. Google does not verify reviews.</p>
      <GoogleReviewCta className="mt-6" />
    </div>
  );
}
